import React, { useState } from "react";
import { useLang } from "../context/LangContext";
import PageHeader from "../components/site/PageHeader";
import { IMG, SERVICE_CATEGORIES } from "../lib/siteData";
import { submitRfq } from "../lib/api";
import { Upload, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { toast, Toaster } from "sonner";

const RFQ = () => {
  const { t } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    country: "",
    product_category: "",
    description: "",
  });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10 MB limit");
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("attachment", file);
      await submitRfq(fd);
      setDone(true);
      toast.success(t("common.success"));
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="rfq-page">
      <Toaster position="top-center" richColors />
      <PageHeader eyebrow={t("nav.rfq")} title={t("rfq.title")} subtitle={t("rfq.subtitle")} image={IMG.pipes} />

      <section className="py-16 lg:py-24 bg-steel-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          {done ? (
            <div className="bg-white border border-steel-200 p-10 lg:p-16 text-center" data-testid="rfq-success">
              <CheckCircle2 className="w-14 h-14 text-brand mx-auto mb-5" strokeWidth={1.4} />
              <h2 className="font-heading font-extrabold text-3xl lg:text-4xl text-ink mb-3">RFQ Submitted</h2>
              <p className="text-ink-muted max-w-xl mx-auto">{t("common.success")} A reference copy has been logged on our system.</p>
              <button onClick={() => { setDone(false); setFile(null); setForm({ company_name: "", contact_person: "", email: "", phone: "", country: "", product_category: "", description: "" }); }} className="mt-8 inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-6 py-3 font-semibold text-sm uppercase tracking-[0.18em]">
                Submit another RFQ <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="bg-white border border-steel-200 p-8 lg:p-12 space-y-6" data-testid="rfq-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label={t("rfq.companyName")} name="company_name" value={form.company_name} onChange={onChange} required testId="rfq-company" />
                <Field label={t("rfq.contactPerson")} name="contact_person" value={form.contact_person} onChange={onChange} required testId="rfq-contact" />
                <Field label={t("rfq.email")} name="email" type="email" value={form.email} onChange={onChange} required testId="rfq-email" />
                <Field label={t("rfq.phone")} name="phone" value={form.phone} onChange={onChange} testId="rfq-phone" />
                <Field label={t("rfq.country")} name="country" value={form.country} onChange={onChange} testId="rfq-country" />
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-soft mb-2">{t("rfq.category")} *</label>
                  <select required name="product_category" value={form.product_category} onChange={onChange} data-testid="rfq-category" className="w-full bg-white border border-steel-300 px-4 py-3 text-sm focus:border-brand focus:outline-none">
                    <option value="">{t("rfq.categoryPlaceholder")}</option>
                    {SERVICE_CATEGORIES.map((c) => (
                      <option key={c.key} value={c.title}>{c.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-soft mb-2">{t("rfq.description")} *</label>
                <textarea required name="description" value={form.description} onChange={onChange} rows={6} data-testid="rfq-description" placeholder="Describe scope, capacity, tag numbers, project location, target schedule…" className="w-full bg-white border border-steel-300 px-4 py-3 text-sm focus:border-brand focus:outline-none resize-y" />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-soft mb-2">{t("rfq.attachment")}</label>
                <label htmlFor="rfq-file" className="block border-2 border-dashed border-steel-300 hover:border-brand bg-steel-50 px-6 py-10 text-center cursor-pointer transition-colors" data-testid="rfq-file-zone">
                  <Upload className="w-7 h-7 text-brand mx-auto mb-3" strokeWidth={1.4} />
                  <p className="text-sm text-ink-soft">{file ? file.name : t("rfq.attachmentHint")}</p>
                  <p className="text-xs text-steel-500 mt-1 font-mono">PDF · XLSX · ZIP · DWG · max 10 MB</p>
                  <input id="rfq-file" type="file" className="hidden" onChange={onFile} data-testid="rfq-file-input" accept=".pdf,.xlsx,.xls,.zip,.dwg,.doc,.docx,.png,.jpg,.jpeg" />
                </label>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-steel-200">
                <p className="text-xs text-steel-500 flex items-start gap-2 max-w-md">
                  <AlertCircle className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                  <span>By submitting, you agree we may contact you regarding this RFQ. Submissions are stored securely.</span>
                </p>
                <button disabled={submitting} type="submit" data-testid="rfq-submit" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white px-8 py-4 font-semibold text-sm uppercase tracking-[0.18em] transition-colors">
                  {submitting ? t("common.loading") : t("common.submitRfq")} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, testId }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-soft mb-2">{label}{required && " *"}</label>
    <input type={type} name={name} value={value} onChange={onChange} required={required} data-testid={testId}
      className="w-full bg-white border border-steel-300 px-4 py-3 text-sm focus:border-brand focus:outline-none" />
  </div>
);

export default RFQ;
